import { FC, ReactNode, forwardRef, PropsWithoutRef, Ref } from 'react';
import styled, { StyledProps, css } from 'styled-components';
import { transparentize } from 'polished';

import { defaultThemeProp } from '../../theme';
import { tryCatch } from '../../utils/utils';
import { StyledCount } from '../Badges/Count';
import { ForwardProps } from '../../types';
import BareButton from '../Button/BareButton';

import { TabsProps } from './Tabs.types';

interface TabProps {
  /** Indicates if this Tab is active. */
  selected: boolean;
  /**
   * Indicates if the styling of this Tab is inverted.
   * @deprecated
   */
  inverted: boolean;
  /** The text to display as the name of the Tab. */
  content: string;
  /** A number to associate with the Tab. */
  count?: ReactNode;
  /** Determines the direction in which the Tab will render. */
  type: TabsProps['type'];
  /** Disables the tab if true */
  disabled?: boolean;
}

export interface StyledTabProps {
  selected: TabProps['selected'];
  inverted: TabProps['inverted'];
  tabType: TabProps['type'];
}

const activeStyle = (color: string) => css`
  ::after {
    display: block;
    background-color: ${color};
  }
`;

const getHorizontalStyles = ({ selected, theme, inverted }: StyledProps<StyledTabProps>) => {
  const primaryColor = inverted
    ? theme.components.tabs.inverted.foreground
    : theme.components.tabs.base.foreground;
  const lightenedColor = tryCatch(() => transparentize(0.3, primaryColor)) ?? '';
  const { spacing } = theme.base;

  return css`
    align-items: center;
    display: flex;
    flex-direction: row;
    height: calc(4 * ${spacing});
    padding: 0 calc(2 * ${spacing});
    border: none;

    span:first-of-type {
      color: ${selected ? primaryColor : theme.base.palette['foreground-color']};
      text-transform: uppercase;
      display: inline-block;

      ::before {
        content: attr(data-content);
        display: block;
        font-weight: bold;
        height: 0;
        overflow: hidden;
        visibility: hidden;
      }
    }

    &:first-child {
      margin-left: 0;
    }

    ::after {
      background: ${selected ? primaryColor : lightenedColor};
      display: ${selected ? 'block' : 'none'};
      content: '';
      position: absolute;
      height: 0.125rem;
      bottom: 0;
      left: 0;
      right: 0;
    }

    &:hover&:not(:disabled) {
      ${activeStyle(lightenedColor)}
    }

    &:focus {
      ${activeStyle(lightenedColor)}
      box-shadow: inset ${theme.base.shadow.focus}
    }

    ${StyledCount} {
      margin-inline-start: calc(0.5 * ${spacing});
    }
  `;
};

const getVerticalStyles = ({ selected, theme }: StyledProps<StyledTabProps>) => {
  const borderColor = theme.base.palette['border-line'];
  const primaryColor = theme.base.palette.interactive;
  const lightenedColor = tryCatch(() => transparentize(0.5, primaryColor)) ?? '';
  const { spacing } = theme.base;

  return css`
    display: flex;
    justify-content: space-between;
    align-items: center;
    border-style: solid;
    color: ${theme.base.palette['foreground-color']};
    border-width: 0.0625rem 0;
    border-color: ${borderColor};
    height: calc(5.5 * ${spacing});
    margin-top: -0.0625rem;
    padding: 0 calc(2 * ${spacing});

    &:first-child {
      border-top: 0.0625rem solid ${borderColor};
    }

    ::after {
      content: '';
      position: absolute;
      display: ${selected ? 'block' : 'none'};
      top: 0;
      bottom: 0;
      right: 0;
      width: 0.25rem;
      background: ${primaryColor};
    }

    &:hover&:not(:disabled) {
      ${activeStyle(lightenedColor)}
    }

    &:focus {
      ${activeStyle(primaryColor)}
      box-shadow: inset 0 0 0 0.0625rem ${primaryColor};
    }
  `;
};

export const StyledTab = styled(BareButton)<StyledTabProps>`
  position: relative;
  background: none;
  cursor: pointer;

  ${props =>
    props.tabType === 'horizontal' ? getHorizontalStyles(props) : getVerticalStyles(props)}
  ${props =>
    props.selected &&
    css`
      font-weight: bold;
    `}
  white-space: nowrap;

  & + & {
    margin-inline-start: 0;
  }

  &:focus {
    outline: none;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: ${props => props.theme.base['disabled-opacity']};
  }
`;

StyledTab.defaultProps = defaultThemeProp;

const Tab: FC<TabProps & ForwardProps> = forwardRef(
  (
    { selected, inverted, content, count, type, disabled, ...restProps }: PropsWithoutRef<TabProps>,
    ref: Ref<HTMLButtonElement>
  ) => {
    return (
      <StyledTab
        ref={ref}
        selected={selected}
        inverted={inverted}
        tabType={type}
        disabled={disabled}
        type='button'
        {...restProps}
      >
        <span data-content={content}>{content}</span>
        {count}
      </StyledTab>
    );
  }
);

export default Tab;
